// src/components/personal/sections/ClientsSection/hooks/useClientInfo.ts
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';

interface UseClientInfoResult {
  clientId: string | null;
  clientInfo: any | null; // Полная информация о клиенте
  isLoading: boolean;
  error: string | null;
  setClientId: (id: string | null) => void;
  refreshClientInfo: () => Promise<void>;
}

/**
 * Хук для работы с детальной информацией о клиенте
 */
export const useClientInfo = (): UseClientInfoResult => {
  useTranslation();
  const { businessId } = useBusinessId();
  
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientInfo, setClientInfo] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Загрузка полной информации о клиенте
   */
  const fetchClientInfo = useCallback(async (id: string) => {
    if (!id || !businessId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Используем новый API-эндпоинт для получения полной информации о клиенте
      const response = await api.get(`/api/clients/${id}/full-info/?business=${businessId}`);
      
      // Сохраняем ответ как есть, без дополнительной обработки
      setClientInfo(response);
      return response;
    } catch (err: any) {
      console.error('Error fetching client info:', err);
      setError(err.message || 'Не удалось загрузить информацию о клиенте');
      setClientInfo(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  /**
   * Обновление данных при изменении ID клиента
   */
  useEffect(() => {
    // Если ID не задан, очищаем данные
    if (!clientId) {
      setClientInfo(null);
      return;
    }
    
    // Загружаем данные клиента
    fetchClientInfo(clientId);
  }, [clientId, fetchClientInfo]);

  /**
   * Обновление данных клиента
   */
  const refreshClientInfo = useCallback(async () => {
    if (clientId) {
      await fetchClientInfo(clientId);
    }
  }, [clientId, fetchClientInfo]);

  return {
    clientId,
    clientInfo,
    isLoading,
    error,
    setClientId,
    refreshClientInfo
  };
};

export default useClientInfo;